import React from 'react'
import { useLocation } from 'react-router'
import { Copy } from 'lucide-react'
import { CVRCompanyDetails, CompanyFinancials, FinancialMetric } from '../../types'
import { Badge, CountryFlagIcon } from '../ui'
import {
  companyCountryFlagCode,
  companyStatusBadgeTone,
  detectCountry,
  formatAddressCountryLabel,
  formatCompanyForm,
  formatDate,
  formatStatusLabel,
} from '../../utils/format'
import { formatKeyFigureAmount } from '../../utils/financial'
import { dataCvrCompanyUrl } from '../../utils/cvrRegisterLinks'
import ProfileActionLinks, { ICON_BTN, PrintCitation, useCopyWithToast } from './ProfileActionLinks'
import { companySourceCitation } from '../../utils/sourceCitation'
import SameAddressCompanies from './SameAddressCompanies'
import type { SameAddressResult } from '../../services/apiClient'

/**
 * Company page header (DESIGN.md → Virksomhedssiden): the name with its
 * flag, a meta line (CVR with copy, form, address and its neighbours),
 * the page actions on the right, and a short facts row underneath.
 * Status only shows when the company is not operating normally — the
 * default needs no badge. Copy confirmations use the global toast
 * provider (`showToast`).
 */
interface IdentityBarProps {
  company: CVRCompanyDetails
  /** When true, skips the primary company name `<h1>` (rendered elsewhere by the parent). */
  omitTitle?: boolean
  /** Primary page action shown last in the button row (e.g. watchlist). */
  titleAction?: React.ReactNode
  financials?: CompanyFinancials | null
  /** Hides the previous company's figures while new ones load. */
  financialsLoading?: boolean
  /** Other companies at the same address; omit to hide the fact. */
  sameAddress?: SameAddressResult | null
}

/** Status as a pill: green dot for »Normal«/»Aktiv«, chip tones otherwise. */
export const StatusPill: React.FC<{ status?: string | null }> = ({ status }) => {
  const label = formatStatusLabel(status)
  const tone = companyStatusBadgeTone(status)
  if (tone === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-active-50 px-2.5 py-[3px] text-[13px] font-semibold text-active">
        <span className="h-1.5 w-1.5 rounded-full bg-active" aria-hidden />
        {label}
      </span>
    )
  }
  return <Badge tone={tone}>{label}</Badge>
}

function companyAddressLine(company: CVRCompanyDetails): string | null {
  const raw = company.adresse?.trim()
  if (!raw) return null
  // Danish addresses drop the »Country: DK« token; foreign ones name the country.
  const street = detectCountry(raw).isForeign
    ? formatAddressCountryLabel(raw)
    : raw.replace(/,?\s*(?:Country|Land):\s*DK\b/i, '').trim()
  const city = [company.postnummer, company.postdistrikt].filter(Boolean).join(' ')
  if (!city || street.includes(city)) return street
  return `${street}, ${city}`
}

/** »Ned 12,4 % fra året før« for the tooltip on a key figure. */
function changeTitle(metric: FinancialMetric): string | undefined {
  const pct = metric.changePercent
  if (pct == null || Number.isNaN(pct) || Math.abs(pct) < 0.05) return undefined
  const formatted = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 }).format(Math.abs(pct))
  return `${pct > 0 ? 'Op' : 'Ned'} ${formatted} % fra året før`
}

function Fact({ label, children, title, testId }: {
  label: string
  children: React.ReactNode
  title?: string
  testId?: string
}) {
  return (
    <div className="flex gap-1.5">
      <dt className="text-ink-500">{label}</dt>
      <dd className="font-semibold text-ink tabular-nums" title={title} data-testid={testId}>
        {children}
      </dd>
    </div>
  )
}

const IdentityBar: React.FC<IdentityBarProps> = ({
  company,
  omitTitle = false,
  titleAction,
  financials = null,
  financialsLoading = false,
  sameAddress = null,
}) => {
  const location = useLocation()
  const copyText = useCopyWithToast()
  const startDate = formatDate(company.startdato)

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}${location.search}`
      : `${location.pathname}${location.search}`

  const cvrStr = String(company.cvrNummer ?? '').trim()
  const datacvrHref = dataCvrCompanyUrl(company.cvrNummer)
  const flagCode = companyCountryFlagCode(company)
  const address = companyAddressLine(company)
  const form = formatCompanyForm(company.virksomhedsform)
  const operating = companyStatusBadgeTone(company.status) === 'active'
  const year = financials?.reportYear ? ` ${financials.reportYear}` : ''
  const citation = companySourceCitation(
    company.navn || `CVR ${cvrStr}`,
    cvrStr,
    shareUrl,
  )

  return (
    <header>
      <div
        className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4"
        data-testid="profile-title-row"
      >
        <div className="min-w-0 flex-1">
          {!omitTitle && (
            <h1 className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[26px] sm:text-[34px] leading-[1.1] text-ink">
              {company.navn || <em className="text-ink-400">Uden navn</em>}
              {flagCode && <CountryFlagIcon code={flagCode} size="lg" />}
            </h1>
          )}
          <div
            className={`flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[14.5px] text-ink-500 ${omitTitle ? '' : 'mt-2.5'}`}
          >
            {!operating && <StatusPill status={company.status} />}
            <span className="inline-flex items-center gap-1 font-semibold text-ink tabular-nums">
              CVR {cvrStr || '—'}
              <button
                type="button"
                onClick={() => copyText(cvrStr, 'CVR-nummer kopieret.')}
                className={`${ICON_BTN} h-[26px] w-[26px]`}
                aria-label="Kopiér CVR-nummer"
                title="Kopiér CVR-nummer"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
            {form && <span>{form}</span>}
            {address && (
              // The neighbours link stays next to the address when the line wraps.
              <span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-2">
                <span>{address}</span>
                {sameAddress && sameAddress.totalCount > 0 && (
                  <>
                    <span className="hidden text-ink-300 sm:inline" aria-hidden>·</span>
                    <SameAddressCompanies result={sameAddress} />
                  </>
                )}
              </span>
            )}
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-7 gap-y-1.5 text-sm">
            {startDate && <Fact label="Stiftet">{startDate}</Fact>}
            {company.ansatte !== undefined && company.ansatte !== null && (
              <Fact label="Ansatte">{company.ansatte}</Fact>
            )}
            {!financialsLoading && financials?.revenue && (
              <Fact label={`Omsætning${year}`} title={changeTitle(financials.revenue)} testId="company-revenue">
                {formatKeyFigureAmount(financials.revenue.amount)} kr.
              </Fact>
            )}
            {!financialsLoading && financials?.profit && (
              <Fact
                label={`Resultat${financials.revenue ? '' : year}`}
                title={changeTitle(financials.profit)}
                testId="company-profit"
              >
                {formatKeyFigureAmount(financials.profit.amount)} kr.
              </Fact>
            )}
          </dl>
        </div>
        <ProfileActionLinks
          shareUrl={shareUrl}
          citation={citation}
          connectionHref={`/forbindelse?til=cvr:${cvrStr}&tilNavn=${encodeURIComponent(company.navn || cvrStr)}`}
          datacvrHref={datacvrHref}
          datacvrAriaLabel="Åbn virksomhed på CVR.dk i nyt vindue"
        >
          {titleAction}
        </ProfileActionLinks>
      </div>
      <PrintCitation citation={citation} />
    </header>
  )
}

export default IdentityBar
